import React from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { utils } from '@makeship/core';

import posthog from 'posthog-js';
import Modal from '..';
import { H5, P2, Caption, Strong, Overline } from '../../Typography';
import { modalTheme } from '../../../styles/themes';
import { ProductType } from '../../../types/common';
import { parseMetafields } from '../../../utils/product';

const ModalContent = styled.div`
  width: 100%;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ModalCaption = styled(Caption)`
  color: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral7, theme.alpha.dark)};
`;

const CancelIcon = styled.div`
  position: absolute;
  right: 32px;
  top: 26px;
  cursor: pointer;
`;

const ModalHeader = styled.div`
  text-align: left;
  margin: 16px 0 18px;
`;

const SizeGuideBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const SizeChartDiagram = styled.div`
  margin-bottom: 18px;
`;

const Picture = styled.picture`
  width: 100%;
  height: auto;
  display: block;
`;

const SizeGuideUnitToggles = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 18px;
  margin-bottom: 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-bottom: 0;
  }
`;

const SizeGuideUnitToggle = styled.div<{ isActive?: boolean }>`
  background: ${({ theme, isActive }) => (isActive ? theme.colors.neutral6 : theme.colors.neutral1)};
  padding: 2px 6px;
  border-radius: 8px;
  cursor: pointer;
  border: ${({ theme }) => `1px solid ${theme.colors.neutral6}`};
`;

const SizeGuideUnitText = styled(Overline)<{ isActive?: boolean }>`
  color: ${({ theme, isActive }) => (isActive ? theme.colors.neutral1 : theme.colors.neutral6)};
`;

const StyledSizeChartImage = styled.img<{ widths?: { mobile: number; desktop: number } }>`
  object-fit: contain;
  width: 100%;
  max-width: ${({ widths }) => (widths ? `${widths.mobile}px` : 'auto')};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    max-width: ${({ widths }) => (widths ? `${widths.desktop}px` : 'auto')};
  }
`;

const SizeGuideModalTheme = {
  ...modalTheme,
  content: {
    ...modalTheme.content,
    borderRadius: '30px',
    width: '800px',
  },
};

type SizeGuideModalProps = {
  productType: ProductType;
  isOpen: boolean;
  closeModal: () => void;
  product: Shopify.Product | undefined;
};

enum SizeGuideUnits {
  US = 'US',
  EU = 'EU',
}

type SizeGuideImageConfig = {
  basePaths: Record<SizeGuideUnits, string>;
  widths?: {
    mobile: number;
    desktop: number;
  };
};

const SizeGuideModal: React.FC<SizeGuideModalProps> = ({
  productType,
  isOpen,
  closeModal,
  product,
}: SizeGuideModalProps) => {
  const mobileBreakpoint = '768';
  const metafields = product && parseMetafields(product.metafields);
  const [selectedUnits, setSelectedUnits] = React.useState<SizeGuideUnits>(SizeGuideUnits.US);

  const renderSizeGuideImage = (productType: ProductType, selectedUnits: SizeGuideUnits) => {
    const imageMap: {
      [key in ProductType]?: SizeGuideImageConfig;
    } = {
      [ProductType.Hoodie]: {
        basePaths: {
          US: '/assets/apparel/hoodies/hoodies_size_chart_in',
          EU: '/assets/apparel/hoodies/hoodies_size_chart_cm',
        },
      },
      [ProductType.Crewneck]: {
        basePaths: {
          US: '/assets/apparel/crewnecks/crewneck_size_chart_in',
          EU: '/assets/apparel/crewnecks/crewneck_size_chart_cm',
        },
      },
      [ProductType.TShirt]: {
        basePaths: {
          US: '/assets/apparel/tshirt/tshirt_size_chart_in',
          EU: '/assets/apparel/tshirt/tshirt_size_chart_cm',
        },
      },
      [ProductType.Sweatpants]: {
        basePaths: {
          US: '/assets/apparel/sweatpants/sweatpants_size_chart_in',
          EU: '/assets/apparel/sweatpants/sweatpants_size_chart_cm',
        },
      },
      [ProductType.CottonSocks]: {
        basePaths: {
          US: '/assets/apparel/socks/socks_size_chart_us',
          EU: '/assets/apparel/socks/socks_size_chart_eu',
        },
        widths: {
          mobile: 296,
          desktop: 436,
        },
      },
    };

    const config = imageMap[productType];
    if (!config) return null;

    const basePath = config.basePaths[selectedUnits];
    if (!basePath) return null;

    const { widths } = config;

    return (
      <Picture>
        <source media={`(max-width: ${mobileBreakpoint}px)`} srcSet={`${basePath}_mobile.png`} />
        <source media={`(min-width: ${mobileBreakpoint}px)`} srcSet={`${basePath}_desktop.png`} />
        <StyledSizeChartImage src={`${basePath}_mobile.png`} alt="Size Chart" widths={widths} />
      </Picture>
    );
  };

  const renderHowToFindYourSizeCopy = (productType: ProductType) => {
    if (productType === ProductType.CottonSocks) {
      return;
    }
    if (productType === ProductType.Hoodie || productType === ProductType.Crewneck) {
      return (
        <>
          <ModalCaption>Finding Your Correct Size</ModalCaption>
          For <Strong>length</Strong>, measure from the top of your shoulder to your waist. For <Strong>width</Strong>,
          measure around your chest under each arm. And for <Strong>sleeve</Strong>, measure from the edge of your
          shoulder to your wrist.
        </>
      );
    }
    if (productType === ProductType.TShirt) {
      return (
        <>
          <ModalCaption>Finding Your Correct Size</ModalCaption>
          For <Strong>body length</Strong>, measure from the top of your shoulder to your waist. For{' '}
          <Strong>chest width</Strong>, measure the distance across your chest to the point under each arm.
        </>
      );
    }

    if (productType === ProductType.Sweatpants) {
      return (
        <>
          <ModalCaption>Finding Your Correct Size</ModalCaption>
          For <Strong>pant length</Strong>, measure the distance from the top of your hip to your ankle. For{' '}
          <Strong>hip width</Strong>, measure the distance across hips from one hip bone to the other.
        </>
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={closeModal} theme={SizeGuideModalTheme}>
      <ModalContent>
        <CancelIcon onClick={closeModal}>
          <Image src="/assets/icons/cancel-icon.svg" width="16" height="16" quality={90} />
        </CancelIcon>
        <ModalHeader>
          <H5>Apparel Size Guide</H5>
        </ModalHeader>
        <SizeGuideBody>
          {product && metafields?.disclaimer && (
            <Overline>
              <i>
                For the {product.title}, {metafields.disclaimer}
              </i>
            </Overline>
          )}
          <SizeGuideUnitToggles>
            <SizeGuideUnitToggle
              isActive={selectedUnits === SizeGuideUnits.US}
              onClick={() => {
                posthog.capture('size_guide_unit_toggle', {
                  unit: SizeGuideUnits.US,
                  product_type: productType,
                });
                setSelectedUnits(SizeGuideUnits.US);
              }}
            >
              <SizeGuideUnitText isActive={selectedUnits === SizeGuideUnits.US}>
                {productType === ProductType.CottonSocks ? 'US Shoe Size' : 'Inches'}
              </SizeGuideUnitText>
            </SizeGuideUnitToggle>
            <SizeGuideUnitToggle
              isActive={selectedUnits === SizeGuideUnits.EU}
              onClick={() => {
                posthog.capture('size_guide_unit_toggle', {
                  unit: SizeGuideUnits.EU,
                  product_type: productType,
                });
                setSelectedUnits(SizeGuideUnits.EU);
              }}
            >
              <SizeGuideUnitText isActive={selectedUnits === SizeGuideUnits.EU}>
                {productType === ProductType.CottonSocks ? 'EU Shoe Size' : 'Centimeters'}
              </SizeGuideUnitText>
            </SizeGuideUnitToggle>
          </SizeGuideUnitToggles>
          <SizeChartDiagram>{renderSizeGuideImage(productType, selectedUnits)}</SizeChartDiagram>
          <div>
            <P2>{renderHowToFindYourSizeCopy(productType)}</P2>
          </div>
        </SizeGuideBody>
      </ModalContent>
    </Modal>
  );
};

export default SizeGuideModal;
